import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Card,
  Grid,
  Alert,
  Container
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { format, isValid, startOfDay, endOfDay } from "date-fns";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase"; // Adjust path to your Firebase config
import theme from "../theme"; // Adjust path to your theme file
import Layout from "../Layout"; // Adjust path to your Layout component
import AppContext from "../AppContext"; // Adjust path to your AppContext
import { CheckCircle } from "@mui/icons-material";

const OverdueOrdersCard = () => {
  const [todayOrders, setTodayOrders] = useState([]);
  const [overdueOrders, setOverdueOrders] = useState([]);
  const [isOffline, setIsOffline] = useState(false);
  const { shopId, isOnShift, t } = useContext(AppContext);

  const today = startOfDay(new Date());
  const fiveDaysAgo = new Date(today);
  fiveDaysAgo.setDate(today.getDate() - 5);
  // Fetch orders from Firestore
  useEffect(() => {
    const fetchOrders = async () => {
      const ordersRef = collection(db, "orders");
      

      // Query for overdue orders (before today and not completed)
      const overdueQuery = query(
        ordersRef,
        where("shopId", "==", shopId),
        where("fulfillmentTime", "<", endOfDay(today)),
        where("fulfillmentTime", ">", startOfDay(fiveDaysAgo)),
        where("orderStatus", "not-in", ["Canceled", "Completed"])
      );

      try {
        const [overdueSnapshot] = await Promise.all([
          getDocs(overdueQuery),
        ]);


        const overdueData = overdueSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          fulfillmentTime: doc.data().fulfillmentTime.toDate(),
        }));

        setOverdueOrders(overdueData);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, [shopId]);

  // Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    setIsOffline(!navigator.onLine);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const getStatusColor = (orderStatus) => {
    if (orderStatus === "Fulfilled") return "#4caf50";
    if (orderStatus === "Unfulfilled") return "#f44336";
    if (orderStatus === "Completed") return "#2979ff";
    return "#757575"; // Default color
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    if (isValid(date)) {
      return format(date, "dd-MM-yy h:mm a");
    }
    return "Invalid Date";
  };

  const formatToMyanmarCurrency = (number) => {
      const formattedNumber = new Intl.NumberFormat('my-MM').format(number);
      return `${formattedNumber}`;
    };

  return (
    <Box sx={{margin:0, padding:0}}>
      <ThemeProvider theme={theme}>
        <Box sx={{ mt:2 }}>

          {/* Overdue Orders Section */}
          {overdueOrders.length > 0 ? (
          <Card variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom color="error">
            {t("Overdue Orders")} ({overdueOrders.length})
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Order ID")}</TableCell>
                  <TableCell>{t("Customer")}</TableCell>
                  <TableCell>{t("Inscription")}</TableCell>
                  <TableCell>{t("Balance")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                  <TableCell>{t("Fulfillment Time")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overdueOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.orderID}</TableCell>
                    <TableCell>
                      {order.customerName} ({order.phoneNumber})
                    </TableCell>
                    <TableCell>
                      {order.inscription}
                    </TableCell>
                    <TableCell>
                      {formatToMyanmarCurrency(order.remainingBalance)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 0,
                            backgroundColor: getStatusColor(order.orderStatus),
                            mr: 1,
                          }}
                        />
                        {t(order.orderStatus)}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDateTime(order.fulfillmentTime)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Card>
          ) : (
          <Card variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3 }}>
            <CheckCircle color="success" sx={{ mr: 1 }} />
            <Typography variant="h6" color="success.main">
              No overdue orders
            </Typography>
          </Box>
          </Card>
          )}
        </Box>
      </ThemeProvider>
    </Box>
  );
};

export default OverdueOrdersCard;
